/**
 * Wire schemas for api-common's own entity-generic bindings and the session
 * reads (preferences, usable settings, permissions).
 */
import * as z from 'zod/mini';
import type {
  AccountingAccountRow,
  FlagRecord,
  Note,
  SessionPermissions,
  SessionPreferences,
  UsableSettings,
  VendorRow,
  WatchSummary,
} from '../types';
import { wireBool, wireInt, wireNumber } from '../wire';
import type { WireSchema } from '../wire';

const oInt = z.optional(z.nullable(wireInt));
const oBool = z.optional(z.nullable(wireBool));
const cast = <T>(schema: z.ZodMiniType): WireSchema<T> => schema as unknown as WireSchema<T>;

/** A body the binding does not read; declared so the registry sees an explicit contract. */
export const unread = cast<unknown>(z.unknown());

// ── flags ────────────────────────────────────────────────────────────────

const flagRecord = z.looseObject({
  enabled: wireBool,
  user_hidden: oBool,
  is_user_flag: oBool,
  pipeline_time_seconds: oInt,
  stage_times: z.optional(z.nullable(z.record(z.string(), wireNumber))),
});
export const flagRecords = cast<FlagRecord[]>(z.array(flagRecord));
export const stageTimes = cast<Record<string, number>>(z.record(z.string(), wireNumber));

// ── notes ────────────────────────────────────────────────────────────────

const note = z.looseObject({ note: z.string() });
export const noteRow = cast<Note>(note);
export const noteRows = cast<Note[]>(z.array(note));
export const noteDraft = cast<{ note?: unknown } | null>(z.nullable(z.looseObject({})));

// ── query / vendors ──────────────────────────────────────────────────────

export const accountingAccountRows = cast<AccountingAccountRow[]>(
  z.array(z.looseObject({ banking_account: wireInt, parent_id: oInt }))
);
export const vendorRow = cast<VendorRow | null>(z.nullable(z.looseObject({})));

// management\query::searchVendors rows; the id is a number on the wire (PHP may send it as a string).
export const vendorRows = cast<VendorRow[]>(
  z.array(z.looseObject({ id: wireInt, name: z.nullable(z.string()), internal_name: z.optional(z.nullable(z.string())) })),
);

// ── session ──────────────────────────────────────────────────────────────

/**
 * `settings::generatePreferences`: a flat key → value map. Boolean
 * preferences are already `boolval`'d server-side, object preferences
 * decoded, everything else a string; values are not re-typed here.
 */
export const sessionPreferences = cast<SessionPreferences>(z.record(z.string(), z.unknown()));

/**
 * `settings::getUsable`: the dealer's whole settings document. Only the
 * session identity fields are declared; `admin_dealer_id` is deliberately a
 * string server-side (`strval`) and stays one.
 */
export const usableSettings = cast<UsableSettings>(
  z.looseObject({
    master_dealer_id: wireInt,
    current_dealer_id: wireInt,
    admin: oBool,
    services: z.optional(z.record(z.string(), z.unknown())),
  })
);

/** `management::getPermissions`: module → permission map, as generated. */
export const sessionPermissions = cast<SessionPermissions>(z.record(z.string(), z.unknown()));

// ── watch summary ────────────────────────────────────────────────────────

const watchSummaryShape = z.looseObject({
  generated_at: wireInt,
  leads: z.nullable(z.looseObject({
    unanswered: wireInt,
    oldest_unanswered_at: z.nullable(wireInt),
    received_today: wireInt,
  })),
  labor: z.nullable(z.looseObject({
    running: z.boolean(),
    ticket_id: z.nullable(wireInt),
    ro_number: z.nullable(wireInt),
    labor_id: z.nullable(wireInt),
    dealer_id: z.nullable(wireInt),
    started_at: z.nullable(wireInt),
  })),
  units: z.nullable(z.looseObject({
    today: wireInt,
    month_to_date: wireInt,
    month_target: z.nullable(wireInt),
  })),
});
export const watchSummary = cast<WatchSummary>(watchSummaryShape);
